/**
 * Inea conversational hook — SSE streaming chat with tool-result events.
 */
import { useCallback, useRef, useState } from 'react';
import i18n from '@/i18n';
import { getToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4007';

export type ChatRole = 'user' | 'assistant';

/** A tool result attached to an assistant message. */
export interface InlineTool {
  name: string;
  payload: unknown;
}

export interface ChatTurn {
  role: ChatRole;
  text: string;
  /** Tool results streamed alongside this assistant turn. */
  tools?: InlineTool[];
  /** Marks the turn while text is still streaming in. */
  streaming?: boolean;
}

export interface AskOptions {
  pageHint?: string;
  openProspectId?: string;
}

/** Live conversation state — history + the in-flight streamed turn. */
export function useInea() {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Lot 1 — plafond IA atteint : information calme, pas une erreur.
  const [quotaReached, setQuotaReached] = useState(false);
  const [busy, setBusy] = useState(false);
  // Abort the in-flight stream when the user starts a new question.
  const abortRef = useRef<AbortController | null>(null);

  const ask = useCallback(
    async (message: string, opts: AskOptions = {}): Promise<void> => {
      if (busy) return;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setBusy(true);
      setError(null);
      // Push the user turn immediately + an empty streaming assistant turn.
      const history = turns.map((t) => ({ role: t.role, content: t.text }));
      setTurns((prev) => [
        ...prev,
        { role: 'user', text: message },
        { role: 'assistant', text: '', tools: [], streaming: true },
      ]);

      try {
        const res = await fetch(`${API_BASE}/inea/chat`, {
          method: 'POST',
          signal: ctrl.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken() ?? ''}`,
          },
          body: JSON.stringify({
            message,
            pageHint: opts.pageHint,
            openProspectId: opts.openProspectId,
            history,
          }),
        });
        if (!res.ok || !res.body) {
          const j = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          const failure = new Error(j.message ?? i18n.t('common.networkError')) as Error & { code?: string };
          failure.code = j.error;
          throw failure;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const line = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            if (!line.startsWith('data: ')) continue;
            const evt = JSON.parse(line.slice(6)) as
              | { type: 'token'; text: string }
              | { type: 'tool'; name: string; payload: unknown }
              | { type: 'done' }
              | { type: 'error'; message: string };
            if (evt.type === 'token') {
              setTurns((prev) => {
                const out = prev.slice();
                const last = out[out.length - 1];
                if (last && last.role === 'assistant') {
                  out[out.length - 1] = { ...last, text: last.text + evt.text };
                }
                return out;
              });
            } else if (evt.type === 'tool') {
              setTurns((prev) => {
                const out = prev.slice();
                const last = out[out.length - 1];
                if (last && last.role === 'assistant') {
                  out[out.length - 1] = {
                    ...last,
                    tools: [...(last.tools ?? []), { name: evt.name, payload: evt.payload }],
                  };
                }
                return out;
              });
            } else if (evt.type === 'error') {
              setError(evt.message);
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        if ((err as Error & { code?: string }).code === 'API_BUDGET_EXCEEDED') {
          setQuotaReached(true);
        } else {
          setError((err as Error).message);
        }
      } finally {
        setBusy(false);
        setTurns((prev) => {
          const out = prev.slice();
          const last = out[out.length - 1];
          if (last && last.role === 'assistant' && last.streaming) {
            out[out.length - 1] = { ...last, streaming: false };
          }
          return out;
        });
      }
    },
    [busy, turns],
  );

  function reset(): void {
    abortRef.current?.abort();
    setTurns([]);
    setError(null);
    setQuotaReached(false);
    setBusy(false);
  }

  return { turns, ask, busy, error, quotaReached, reset };
}
